import type { IoTNode, ParcelValveBinding } from '../../lib/iotApi';
import { buildSectorModel } from '../../lib/sectorOperationalModel';
import { parcelValveState } from '../../lib/parcelOperational';
import { toSoverdiSectorView, type SoverdiSectorView } from './agriSafeDashboardAdapter';
import { normalizeCustomDisplayName, sectorNameKey } from './sectorNaming';

export function bindingDisplayName(binding: ParcelValveBinding, names: Map<string, string>): string {
  return normalizeCustomDisplayName(names.get(sectorNameKey(binding.site, `binding:${binding.binding_id}`))) ?? binding.parcel;
}

/** Preserve explicitly attributed pressure and unambiguous soil telemetry.
 * Shared controller flow is optional for analysis, where its scope is labelled.
 */
export function installedSectorNodes(input: {
  binding: ParcelValveBinding;
  bindings: ParcelValveBinding[];
  nodes: IoTNode[];
  pressureSensorByBinding: Record<string, string>;
  sector?: SoverdiSectorView;
  placement?: Map<string, { siteName: string; sectorCode: string }>;
  includeSharedController?: boolean;
}): IoTNode[] {
  const { binding, bindings, nodes, pressureSensorByBinding, sector, placement, includeSharedController } = input;
  const confirmed = bindings.filter(b => b.binding_status === 'CONFIRMED');
  const unambiguousSoil = confirmed.filter(b => b.site === binding.site && b.sector_code === binding.sector_code).length === 1
    && sector?.canonicalParcelNames?.length === 1;
  const ownController = includeSharedController || confirmed.filter(b => b.controller_node_id === binding.controller_node_id).length === 1;
  const counts = new Map<string, number>();
  for (const node of nodes) counts.set(node.id, (counts.get(node.id) ?? 0) + 1);
  return nodes.filter(node => {
    if (counts.get(node.id) !== 1) return false;
    if (node.id === pressureSensorByBinding[binding.binding_id]) return true;
    if (ownController && node.id === binding.controller_node_id) return true;
    const assigned = placement?.get(node.sector_id ?? '');
    return unambiguousSoil && node.model === 'soil_air_ecth'
      && assigned?.siteName === binding.site && assigned?.sectorCode === binding.sector_code;
  });
}

/** One installed irrigation unit per binding, independent of shared legacy groups.
 * Never copy a grouped override or a neighbouring parcel's telemetry.
 */
const UNKNOWN_STATE = 'unknown' as const;
const STOPPED_STATE = 'stopped' as const;

export function buildInstalledSectorViews(input: {
  placement?: Map<string, { siteName: string; sectorCode: string }>;
  sectors: SoverdiSectorView[];
  bindings: ParcelValveBinding[];
  nodes: IoTNode[];
  pressureSensorByBinding: Record<string, string>;
  customNames: Map<string, string>;
  now: number;
}): SoverdiSectorView[] {
  const { sectors, bindings, nodes, pressureSensorByBinding, customNames, now, placement } = input;
  const installed = bindings.filter(b => b.site === 'Roubia' && b.binding_status === 'CONFIRMED');
  const result = installed.map(binding => {
    // Only the explicitly associated pressure sensor is eligible. Shared flow
    // remains absent here; its per-binding backend supervision owns attribution.
    const commissionedNodes = installedSectorNodes({ binding, bindings, nodes, pressureSensorByBinding, placement,
      sector: sectors.find(s => s.siteName === binding.site && s.sectorCode === binding.sector_code) });
    const model = buildSectorModel({ nodes: [], placement: new Map(),
      siteName: binding.site, sectorCode: binding.sector_code, now,
      commissionedNodes,
    });
    const view = toSoverdiSectorView(model, binding.site, binding.sector_code, {
      parcelNames: [binding.parcel],
      customDisplayName: customNames.get(sectorNameKey(binding.site, `binding:${binding.binding_id}`)),
    });
    const controllers = nodes.filter(n => n.id === binding.controller_node_id);
    const state = parcelValveState(controllers.length === 1 ? controllers[0] : undefined, binding.channel_id, now);
    return { ...view, id: `${binding.site}:binding:${binding.binding_id}`, bindingId: binding.binding_id,
      valveState: state === 'open' || state === 'closed' ? state : UNKNOWN_STATE,
      operationalState: state === 'open' ? 'active' : state === 'closed' ? STOPPED_STATE : UNKNOWN_STATE,
      status: state === 'open' ? 'RUNNING' : state === 'closed' ? 'OFF' : 'UNKNOWN',
    } as SoverdiSectorView;
  });
  return [...result, ...sectors.filter(s => s.siteName !== 'Roubia')];
}
